// @flow

import { Component } from 'react';
import type { Node } from 'react';
import { BrowserStorage } from 'shared_services/riseart/storage/Browser';
import { STORAGE_TYPES } from 'shared_services/riseart/storage/Interfaces';

import type { Storage, StorageTypes } from 'shared_services/riseart/storage/Interfaces';

type Props = {
  adapter?: Storage,
  storageType?: StorageTypes,
  storageKey: string,
  value: any,
  dataMapper?: Function,
  limit?: number,
  children?: Function,
};

/**
 * storeValue
 * @param {Storage} adapter
 * @param {string} storageKey
 * @param {any} value
 */
function storeValue(adapter: Storage, { storageKey, value, dataMapper, limit }: Props) {
  if (!value) {
    return;
  }

  const loadedData = adapter.load(storageKey);
  const storedData =
    typeof dataMapper === 'function' ? dataMapper(loadedData, value, limit) : value;
  adapter.save(storageKey, storedData);

  return { data: value };
}

/**
 * storageAppendUniqueItems
 * @param {Array<?Object>} currentStoredData
 * @param {newValue} Array
 * @param {number} limit
 * @returns {Array<Object>} new data to store, combined with old data and new value at the end. if value already exists in the aray, it will be added to the beginning of the array
 */
export function storageAppendUniqueItems(
  currentStoredData: Array<?Object> = [],
  newValue: Object,
  limit?: number,
): Array<Object> {
  const filteredData =
    currentStoredData &&
    currentStoredData.filter &&
    currentStoredData.filter((item) => item && item.id !== newValue.id);

  if (limit && limit > 0) {
    filteredData.splice(limit - 1);
  }

  return [newValue, ...filteredData];
}

/**
 * StorageProvider
 */
export class StorageProvider extends Component<Props> {
  adapter: Storage;

  static defaultProps: Object = { storageType: STORAGE_TYPES.localStorage };

  /**
   * constructor
   * @param {Props} props
   */
  constructor(props: Props) {
    super(props);

    this.adapter =
      props.adapter || new BrowserStorage(props.storageType || STORAGE_TYPES.localStorage);
    storeValue(this.adapter, props);
  }

  /**
   * componentDidUpdate
   */
  componentDidUpdate() {
    storeValue(this.adapter, this.props);
  }

  /**
   * render
   */
  render(): Node {
    const { children } = this.props;

    return children || null;
  }
}
